/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { useState, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Button, Grid, Input, Radio, Form, Item, Icon, Label } from 'semantic-ui-react'
import { loginToProvider, loginToScope } from '../../services/Auth/Auth.service'
import { useTranslation } from 'react-i18next'
import { isProviderAPos } from '../../services/Provider/Provider.service'
import { ultyToast } from '../Shared/UltyToast/UltyToast'

const MAX_DISPLAYED_OWN_SCOPE_PROVIDERS = 10

const ProviderChoice = ({providers, email, loginContext, afterSubmit}) => {
    const [t] = useTranslation()

    const [loading, setLoading] = useState(false)
    const [search, setSearch] = useState('')
    const [providerType, setProviderType] = useState('POINT_OF_SALE')
    const [maxDisplayedOwnScopeProviders, setMaxDisplayedOwnScopeProviders] = useState(MAX_DISPLAYED_OWN_SCOPE_PROVIDERS)

    useEffect(() => {
        if (providers.length === 1) {
            setProviderType(providers[0].type)
        }
    }, [providers])

    const getProviders = useCallback(() => {
        return providers.filter(p => {
            return (p.type === providerType || (providerType === 'POINT_OF_SALE' && isProviderAPos(p))) && (search === '' || p.object.name.toLowerCase().indexOf(search.toLowerCase()) > -1)
        })
    }, [providers, search, providerType])

    const handleSelect = async (providerType, providerId) => {
        setLoading(true)

        try {
            let data
            if (providerType === 'OWN_SCOPE') {
                data = await loginToScope(providerType, email)
            } else {
                data = await loginToProvider(providerId, email)
            }

            setLoading(false)
            afterSubmit(data)
        } catch (e) {
            ultyToast({ type: 'error', title: t('global.anErrorOccurred') })
        }

        setLoading(false)
    }

    const handleChangeProviderType = (providerType) => {
        setProviderType(providerType)
        setMaxDisplayedOwnScopeProviders(MAX_DISPLAYED_OWN_SCOPE_PROVIDERS)
    }

    return (
      <>
          <Grid>
              <Grid.Column computer={8} tablet={8} mobile={16}>
                  {providerType !== 'OWN_SCOPE' &&
                    <Input
                      fluid
                      icon="search"
                      placeholder={t('product_list.filters.filter_placeholder')}
                      value={search}
                      onChange={(evt, {value}) => setSearch(value)}
                    />}
              </Grid.Column>
              <Grid.Column computer={8} tablet={8} mobile={16} css={css`
                  display: flex !important;
                  flex-direction: row !important;
                  align-items: center !important;
                  justify-content: space-evenly !important;
              `}>
                  <Form.Field>
                      <Radio
                        label={t('global.merchant')}
                        name="checked"
                        disabled={providers.length === 1}
                        checked={providerType === 'POINT_OF_SALE'}
                        onChange={() => {
                            handleChangeProviderType('POINT_OF_SALE')
                        }}
                      />
                  </Form.Field>
                  <Form.Field>
                      <Radio
                        label={t('global.own_scope')}
                        name="checked"
                        disabled={providers.length === 1}
                        checked={providerType === 'OWN_SCOPE'}
                        onChange={() => {
                            handleChangeProviderType('OWN_SCOPE')
                        }}
                      />
                  </Form.Field>
                  <Form.Field>
                      <Radio
                        label={t('global.company')}
                        name="checked"
                        disabled={providers.length === 1}
                        checked={providerType === 'COMPANY'}
                        onChange={() => {
                            handleChangeProviderType('COMPANY')
                        }}
                      />
                  </Form.Field>
              </Grid.Column>
          </Grid>
          <Item.Group divided>
              {providerType === 'OWN_SCOPE' &&
                <Item
                  css={css`
                      flex-direction: row !important;
                      align-items: flex-start;
                  `}
                >
                    <Item.Content>
                        <Item.Header>{t('login.x_points_of_sale', {count: loginContext.providers.filter(isProviderAPos).length})}</Item.Header>
                        <>
                            {loginContext.providers
                              .filter(isProviderAPos)
                              .sort((a, b) => a.object.name.localeCompare(b.object.name))
                              .slice(0, maxDisplayedOwnScopeProviders)
                              .map(provider => (
                                <Item.Meta key={provider.id}>{provider.object.name}</Item.Meta>
                              ))}
                            {loginContext.providers.filter(isProviderAPos).length > maxDisplayedOwnScopeProviders && (
                              <Label
                                basic
                                onClick={() => {
                                    setMaxDisplayedOwnScopeProviders(loginContext.providers.filter(isProviderAPos).length)
                                }}
                                css={css`
                                    cursor: pointer;
                                `}
                              >
                                  {t('global.see_more')}
                              </Label>
                            )}
                        </>
                    </Item.Content>
                    <Button basic color="teal" icon loading={loading} disabled={loading}
                            onClick={() => handleSelect(providerType, null)}
                    >
                        <Icon name="arrow right"/>
                    </Button>
                </Item>
              }
              {getProviders().sort((a, b) => a.object.name.localeCompare(b.object.name)).map(provider => (
                <Item
                  key={provider.id}
                  color={provider.type === 'COMPANY' ? 'blue' : 'green'}
                  css={css`
                      flex-direction: row !important;
                  `}>
                    <Item.Content>
                        <Item.Header>{provider.object.name}</Item.Header>
                        <Item.Meta>{t(`provider.type_${provider.type}`)}</Item.Meta>
                    </Item.Content>
                    <Button basic color="teal" icon loading={loading} disabled={loading}
                            onClick={() => handleSelect(provider.type, provider.id)}
                            css={css`
                                align-self: flex-start;
                            `}
                    >
                        <Icon name="arrow right"/>
                    </Button>
                </Item>
              ))}
          </Item.Group>
      </>
    )
}

ProviderChoice.propTypes = {
    providers: PropTypes.arrayOf(PropTypes.object).isRequired,
    email:
    PropTypes.string.isRequired,
    afterSubmit:
    PropTypes.func.isRequired,
}

export default ProviderChoice
